import React from 'react';

import renderExpandedListing from 'nav/expanded/RenderExpandedListing';
import {ListableItem, Sort} from 'search/SearchConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import ExpandedScrollableTable from 'ui-library/scrollable/ExpandedScrollableTable';
import {
  RowSelectionMode,
  ScrollableTableRow,
  TABLE_KEYS,
  TABLE_ROW_BODY_HEIGHT,
} from 'ui-library/scrollable/ScrollableTableConstants';
import {cycleColumnHeaderSort, makeFirstColumnFixed} from 'ui-library/scrollable/ScrollableTableUtil';

import {WorklistSummaryGraphQL} from '../../../graphql-fragments/worklists/__generated/WorklistSummaryGraphQL';

export interface ExpandedWorklistsTableProps {
  isCompact: boolean;
  onOpenWorklist: (worklistSummary: WorklistSummaryGraphQL) => void;
  onToggleSelectedWorklistIds: (ids: Array<string>, isSelected: boolean) => void;
  selectedWorklistIds: Array<string>;
  setSort: (key: string, reverse: boolean) => void;
  sorts: Array<Sort>;
  worklists: Array<WorklistSummaryGraphQL> | null;
}

export default function ExpandedWorklistsTable(props: ExpandedWorklistsTableProps): JSX.Element {
  if (!props.worklists) {
    return <CenteredHorizontalThrobber />;
  }

  if (props.worklists.length === 0) {
    return <div className="sidepanel-fadedMessage expandedBrowser-emptyMessage">No worklists found.</div>;
  }

  // Since we aren't passing groups, we can ignore the group related return values safely.
  const {columns, rowDataByItemId, iconsByItemId, rowContentHeightByItemId} = renderExpandedListing(
    props.worklists as Array<ListableItem>,
    props.sorts,
    {}
  );

  const rows: Array<ScrollableTableRow> = props.worklists.map((worklist) => ({
    id: worklist.id,
    data: rowDataByItemId[worklist.id],
    icon: iconsByItemId[worklist.id],
    isSelected: props.selectedWorklistIds.includes(worklist.id),
    extra: {worklist},
  }));

  function handleSetSort(sortKey: string): void {
    const newSort = cycleColumnHeaderSort(props.sorts, sortKey);
    props.setSort(newSort.key, newSort.reverse);
  }

  function handleRowClick(row: ScrollableTableRow): void {
    // We always pass {worklist} to the 'extra' key in creating the
    // rows (see the lines just above), so row.extra.worklist is fine.
    // However, 'extra' is typed as an generic object so callers can pass any
    // additional information they want, so we have to tell typescript to ignore
    // typechecks for this line.
    // @ts-expect-error
    const worklist = row.extra.worklist;
    props.onOpenWorklist(worklist);
  }

  return (
    <ExpandedScrollableTable
      canFilterColumns={true}
      canResizeColumns={true}
      rowSelectionMode={RowSelectionMode.MULTIPLE}
      className="u-flex-grow"
      columns={makeFirstColumnFixed(columns)}
      getRowContentHeight={(rowIndex: number) =>
        props.worklists ? rowContentHeightByItemId[rows[rowIndex].id] : TABLE_ROW_BODY_HEIGHT
      }
      isCompact={props.isCompact}
      matchParentHeight={true}
      onChangeSort={handleSetSort}
      onRowClick={handleRowClick}
      onRowsSelect={(selectedRows: Array<ScrollableTableRow>, isSelected: boolean) => {
        props.onToggleSelectedWorklistIds(
          selectedRows.map((r) => r.id),
          isSelected
        );
      }}
      rows={rows}
      tableKey={TABLE_KEYS.EXPANDED_WORKLIST_TABLE}
    />
  );
}
